import { Component, inject } from '@angular/core';
import { PatientRegistrationDetailsComponent } from '../patient-registration-details/patient-registration-details.component';
import { HttpClient } from '@angular/common/http';
import { Patient, Practitioner } from 'fhir/r4';
import { authBackendEndPointToken } from '../app.config';
import { AuthService } from '../shared/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { PatientRegistrationEditComponent } from '../patient-registration-edit/patient-registration-edit.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { ErrorService } from '../shared/error.service';
import { json } from 'express';
import { isPatientRole, normalizeRole, SystemRoles } from '../shared/system-roles';
import { ChangePasswordModalComponent } from '../change-password-modal/change-password-modal.component';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MyHospitalsComponent } from '../shared/my-hospitals/my-hospitals.component';
import { MatTabsModule } from '@angular/material/tabs';




interface SuperAdminProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  fullName: string;
  status: string;
}


@Component({
  selector: 'app-profile',
  imports: [
    PatientRegistrationDetailsComponent,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDividerModule,
    MyHospitalsComponent,
    MatTabsModule,
    FormsModule
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})


export class ProfileComponent {
  profileData?: any;
superAdminProfile?: SuperAdminProfile;

  isEditingSuperAdminProfile = false;
  editFirstName = '';
  editLastName = '';
  editPhoneNumber = '';
  isSavingSuperAdminProfile = false;
  http = inject(HttpClient);
  auth = inject(AuthService);
  backendApiBaseUrl = inject(authBackendEndPointToken)
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private errorService = inject(ErrorService);
 private readonly ROLES_WITHOUT_FHIR_RESOURCE = new Set<string>([
    SystemRoles.SuperAdmin,
    SystemRoles.GlobalEventManager,
  ]);

 ngOnInit() {
    this.auth.user.subscribe(user => {
      if (!user) return;

      const role = normalizeRole(user.role);

      if (role === SystemRoles.SuperAdmin) {
        this.http.get<SuperAdminProfile>(`${this.backendApiBaseUrl}/api/SuperAdmin/users/${user.id}`)
          .subscribe({
            next: (data) => { this.superAdminProfile = data; },
            error: () => { this.errorService.openandCloseError('Failed to load profile'); }
          });
        return;
      }

      if (!user.fhirResourceId || this.ROLES_WITHOUT_FHIR_RESOURCE.has(role)) {
        this.profileData = undefined;
        return;
      }

      const resourceType = isPatientRole(role) ? 'Patient' : 'Practitioner';
      const cacheBuster = Date.now();

      this.http.get<Patient>(`${this.backendApiBaseUrl}/fhir/${resourceType}/${user.fhirResourceId}?_=${cacheBuster}`).subscribe({
        next: (allData) => { this.profileData = allData; },
        error: (err) => {
          const msg = err?.status === 404 ? 'No clinical profile associated with this account' : 'Failed to load profile';
          this.errorService.openandCloseError(msg);
        }
      });
    });
  }

  startEditingSuperAdminProfile() {
    if (!this.superAdminProfile) return;
    this.editFirstName = this.superAdminProfile.firstName;
    this.editLastName = this.superAdminProfile.lastName;
    this.editPhoneNumber = this.superAdminProfile.phoneNumber ?? '';
    this.isEditingSuperAdminProfile = true;
  }

  cancelEditingSuperAdminProfile() {
    this.isEditingSuperAdminProfile = false;
  }

  saveSuperAdminProfile() {
    if (!this.superAdminProfile) return;

    const updates = {
      firstName: this.editFirstName.trim(),
      lastName: this.editLastName.trim(),
      phoneNumber: this.editPhoneNumber.trim() || undefined
    };

    this.isSavingSuperAdminProfile = true;

    this.http.put(`${this.backendApiBaseUrl}/api/SuperAdmin/users/${this.superAdminProfile.id}`, updates)
      .subscribe({
        next: () => {
          this.superAdminProfile = {
            ...this.superAdminProfile!,
            ...updates,
            fullName: `${updates.firstName} ${updates.lastName}`
          };
          this.isSavingSuperAdminProfile = false;
          this.isEditingSuperAdminProfile = false;
          this.snackBar.openFromComponent(SuccessMessageComponent, {
            data: { message: 'Profile updated successfully.', action: 'Close' },
            duration: 3000, horizontalPosition: 'center', verticalPosition: 'top'
          });
        },
        error: () => {
          this.isSavingSuperAdminProfile = false;
          this.errorService.openandCloseError('Failed to update profile');
        }
      });
  }
get showHospitals(): boolean {
  const u = this.auth.user.getValue();
  return !!u && !u.isSuperAdmin && normalizeRole(u.role) !== SystemRoles.Patient;
}
  openChangePasswordModal(): void {
    const dialogRef = this.dialog.open(ChangePasswordModalComponent, {
      width: '550px',
      maxWidth: '90vw',
      panelClass: 'change-password-dialog',
      disableClose: false,
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.snackBar.openFromComponent(SuccessMessageComponent, {
          data: { message: 'Password changed successfully.', action: 'Close' },
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      }
    });
  }
  onEditRequested(resource: Patient | Practitioner) {
    const ref = this.dialog.open(PatientRegistrationEditComponent, {
      data: { resource, context: 'profile', isEditModeForPractitioner: resource.resourceType === 'Practitioner' },
      width: '720px',
      maxWidth: '92vw',
      maxHeight: '93vh'
    });

    ref.afterClosed().subscribe(result => {
      console.log('Dialog closed with result:', result);
      if (result?.updated) {
        const toSave: Patient | Practitioner = result.resource ?? resource;
        if (!toSave.id || !toSave.resourceType) {
          this.errorService.openandCloseError('Updated resource is missing identifiers');
          return;
        }

        this.http.put<Patient | Practitioner>(`${this.backendApiBaseUrl}/fhir/${toSave.resourceType}/${toSave.id}`, toSave, {
          headers: { 'Content-Type': 'application/fhir+json' }
        }).subscribe({
          next: (saved) => {
            this.profileData = saved;
            this.snackBar.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Profile updated successfully.', action: 'Close' },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          },
          error: () => {
            this.errorService.openandCloseError('Failed to update profile');
          }
        });
      }
    });
  }
}
